import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
	AbsoluteCenter,
	Box,
	Button,
	Divider,
	Link,
	Stack,
	Skeleton,
} from "@chakra-ui/react";
import clsx from "clsx";
import { isBefore } from "date-fns";
import React, { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { logEvent } from "src/utlis/analytics";
import { NothingToSeeYet } from "../nothing-to-see-yet";
import { EmploymentHistoryItem } from "./employment-history-item";
import { gql } from "@apollo/client";
import { PortfolioEmploymentHistoryItem } from "src/types";
const itemsPerPage = 3;

interface EmploymentHistoryProps {
	items: PortfolioEmploymentHistoryItem[];
	editable?: boolean;
	loading?: boolean;
	onItemSaved?: () => void;
}

const constructKey = (item: PortfolioEmploymentHistoryItem) => {
	return item.companyName + item.jobTitle + item.description;
};

const TOGGLE_PIN = gql`
	mutation ToggleEmploymentHistoryItemPin($id: String!) {
		togglePortfolioEmploymentHistoryItemPin(id: $id) {
			id
			isPinned
		}
	}
`;

export function EmploymentHistory({
	items: unorderedItems,
	editable,
	loading = false,
	onItemSaved,
}: EmploymentHistoryProps) {
	const [togglePin, togglePinHandle] = useMutation(TOGGLE_PIN);

	const handlePinToggle = async (id?: string | null) => {
		if (!id) {
			return;
		}

		togglePin({
			variables: {
				id,
			},
		});

		logEvent("pro_portfolio/employment_history_pin_toggle", { id });
	};

	const { pinnedItems, items } = useMemo(() => {
		const pinnedItems: PortfolioEmploymentHistoryItem[] = [];
		const rest: PortfolioEmploymentHistoryItem[] = [];

		unorderedItems.forEach((item) => {
			if (item.isPinned) {
				pinnedItems.push(item);
			} else {
				rest.push(item);
			}
		});

		const items = rest.slice().sort((a, b) => {
			if (editable) {
				/**
				 * Sort list by creation in edit mode
				 */
				return a.createdAt && b.createdAt
					? isBefore(new Date(a.createdAt), new Date(b.createdAt))
						? 1
						: -1
					: 0;
			}

			// For non-editable mode, prioritize the current job
			if (a.endedAt === null && b.endedAt !== null) return -1;
			if (a.endedAt !== null && b.endedAt === null) return 1;

			// Then sort by start date (most recent first)
			return a.startedAt && b.startedAt
				? isBefore(new Date(a.startedAt), new Date(b.startedAt))
					? 1
					: -1
				: 0;
		});

		return { pinnedItems, items };
	}, [unorderedItems, editable]);

	const canPin = pinnedItems.length < 3;

	const [currentPage, setCurrentPage] = useState(1);

	const totalPages = Math.ceil(items.length / itemsPerPage);

	const handlePageChange = (page: number) => {
		logEvent("pro_portfolio/employment_history_page_change", { page });
		setCurrentPage(page);
	};

	const currentPageItems = items.slice(
		(currentPage - 1) * itemsPerPage,
		currentPage * itemsPerPage
	);

	return (
		<>
			<div className="flex flex-col gap-8 w-full">
				{loading ? (
					<Stack spacing={4} width="100%">
						{[1, 2, 3].map((i) => (
							<Box key={i} p={5} border="1px solid #E7E7E7">
								<Stack spacing={4}>
									<Skeleton height="18px" width="40%" />
									<Skeleton height="16px" width="30%" />
									<Skeleton height="16px" width="90%" />
								</Stack>
							</Box>
						))}
					</Stack>
				) : (
					<div className="flex flex-col gap-2">
						<div className="flex flex-col gap-4">
							{pinnedItems.map((item, index, array) => (
								<EmploymentHistoryItem
									key={constructKey(item)}
									item={item}
									className={clsx(index === array.length - 1 && "border-b-0")}
									editable={editable}
									pinned
									onPinToggle={() => handlePinToggle(item.id)}
									isLoading={togglePinHandle.loading}
									onSaved={onItemSaved}
								/>
							))}
						</div>
						{pinnedItems.length > 0 && (
							<Box position="relative" padding="10">
								<Divider />
								<AbsoluteCenter bg="white" px="4">
									All Employment History
								</AbsoluteCenter>
							</Box>
						)}

						{unorderedItems.length === 0 && (
							<Stack
								border={"1px solid #C9C9C9"}
								p={8}
								display={"flex"}
								flexDirection={"column"}
								alignItems={"center"}
								justifyContent={"center"}
								spacing={3}
								textAlign={"center"}
								w={"full"}
							>
								<NothingToSeeYet />
								<p className="text-[#464646] text-sm lg:text-base font-normal">
									Your employment history is currently empty. Add your work
									experience to showcase your skills and experience.
								</p>
								<Link
									href="/my-laborpass/add-job?job-type=employment"
									color={"prussianBlue.500"}
									fontSize={"sm"}
								>
									<p className="text-[#77372E] underline text-base lg:text-lg font-medium">
										Add employment history
									</p>
								</Link>
							</Stack>
						)}

						<div className="flex flex-col gap-4">
							{currentPageItems.map((item, index, array) => (
								<EmploymentHistoryItem
									key={constructKey(item)}
									item={item}
									className={clsx(
										index === array.length - 1 &&
											totalPages === 1 &&
											"border-b-0"
									)}
									editable={editable}
									canPin={canPin}
									onPinToggle={() => handlePinToggle(item.id)}
									isLoading={togglePinHandle.loading}
									onSaved={onItemSaved}
								/>
							))}
						</div>
					</div>
				)}

				{!loading && totalPages > 1 && (
					<div className="flex justify-end items-center gap-4">
						<Button
							isDisabled={currentPage === 1}
							leftIcon={<ChevronLeftIcon />}
							onClick={() => handlePageChange(currentPage - 1)}
						>
							Previous
						</Button>

						<p>
							{currentPage} of {totalPages}
						</p>

						<Button
							isDisabled={currentPage === totalPages}
							rightIcon={<ChevronRightIcon />}
							onClick={() => handlePageChange(currentPage + 1)}
						>
							Next
						</Button>
					</div>
				)}
			</div>
		</>
	);
}
